import CustomerSignUpSvg from 'components/_common/Icons/SVGIcons/CustomerSignUpSvg';
import FFLSignUpSvg from 'components/_common/Icons/SVGIcons/FFLSignUpSvg';
import { t } from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import 'modules/customer/assets/scss/customerLoginModal.scss';

import BuyerSignUpFormSection from './Sections/BuyerSignUpFormSection';
import FFLSignUpFormSection from './Sections/FFLSignUpFormSection';
import LoginFormSection from './Sections/LoginFormSection';

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

interface LoginModalProps {
  onClose: () => void;
  initialTab?: 'buyer' | 'dealer';
  initialFormType?: 'login' | 'signup';
  // a customer who logs in stays on the current page instead of going to the home page
  stayOnPageAfterLogin?: boolean;
}

const LoginModal: React.FC<LoginModalProps> = ({
  onClose,
  initialTab = 'buyer',
  initialFormType = 'login',
  stayOnPageAfterLogin = false,
}) => {
  const [activeTab, setActiveTab] = useState<'buyer' | 'dealer'>(initialTab);
  const [formType, setFormType] = useState<'login' | 'signup'>(initialFormType);

  const ref = useRef<HTMLDivElement>(null);

  /* On mobile the modal covers the whole screen, so freeze the page behind it.
     The lock itself lives in SCSS and only applies below the mobile breakpoint. */
  useEffect(() => {
    document.body.classList.add('login-modal-open');

    // iPhone / iPad Safari only: focusing a field under 16px zooms the page in, which shoves this
    // fixed popup half off the screen. Pin the zoom level while the popup is open (field text
    // sizes stay as designed) and put the viewport back as it was when it closes.
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    const originalViewport = viewport?.getAttribute('content') ?? null;
    if (isIOS && viewport && originalViewport) {
      viewport.setAttribute('content', `${originalViewport}, maximum-scale=1`);
    }

    return () => {
      document.body.classList.remove('login-modal-open');
      if (isIOS && viewport && originalViewport !== null) {
        viewport.setAttribute('content', originalViewport);
      }
    };
  }, []);

  const getHeaderTitle = () => {
    if (formType === 'signup') {
      return activeTab === 'buyer' ? t('sign_up') : t('ffl_sign_up');
    }
    return t('login_or_create_an_account');
  };

  return (
    <div className="login-modal">
      <div className="login-modal-content" ref={ref}>
        {/* Header */}
        <div className="login-modal-content__header-bar">
          <h3>{getHeaderTitle()}</h3>
          <button className="login-modal-content__close-btn" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        {/* Tab Cards */}
        <div className="login-modal-content__tabs">
          <div
            className={`login-modal-content__tab-card ${activeTab === 'buyer' ? 'active' : ''}`}
            onClick={() => setActiveTab('buyer')}
          >
            <div className="tab-icon-wrap">
              <CustomerSignUpSvg />
            </div>
            <div className="tab-content">
              <h4>{t('buyer')}</h4>
              <span>{t('for_individual_buyers_caps')}</span>
            </div>
          </div>

          <div
            className={`login-modal-content__tab-card ${activeTab === 'dealer' ? 'active' : ''}`}
            onClick={() => setActiveTab('dealer')}
          >
            <div className="tab-icon-wrap">
              <FFLSignUpSvg />
            </div>
            <div className="tab-content">
              <h4>{t('ffl_dealer')}</h4>
              <span>{t('for_federal_firearm_licensees_caps')}</span>
            </div>
          </div>
        </div>

        {/* Active Form Body */}
        {formType === 'login' ? (
          <LoginFormSection
            activeTab={activeTab}
            onClose={onClose}
            stayOnPageAfterLogin={stayOnPageAfterLogin}
            onSwitchToSignUp={(targetTab) => {
              if (targetTab) setActiveTab(targetTab);
              setFormType('signup');
            }}
            onSwitchToLogin={(targetTab) => {
              if (targetTab) setActiveTab(targetTab);
              setFormType('login');
            }}
          />
        ) : activeTab === 'buyer' ? (
          <BuyerSignUpFormSection onClose={onClose} onSwitchToLogin={() => setFormType('login')} />
        ) : (
          <FFLSignUpFormSection onClose={onClose} onSwitchToLogin={() => setFormType('login')} />
        )}
      </div>
    </div>
  );
};

export default LoginModal;
